import { API, graphqlOperation, Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { Spinner } from "../../../components/Spinner";
import { Button } from "../../../components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { API_DOMAIN, API_DEFAULT_HEADERS } from "../../../APIconfig"; 

export type StripeStatus = 'free' | 'light' | 'premium' | undefined;

export interface PricingPlan {
  name: string;
  description: string;
  monthlyPrice: string;
  yearlyPrice: string;
  yearlySavings: string;
  features: string[];
  highlighted?: boolean;
  monthlyPriceId: string;
  yearlyPriceId: string;
}

export const pricingPlans: PricingPlan[] = [
  {
    name: 'Light',
    description: 'Voor toegang tot alle Nederlandse aanbestedingen',
    monthlyPrice: '8,95',
    yearlyPrice: '89,50',
    yearlySavings: '17,90',
    features: [
      'Onbeperkt aanbestedingen bekijken',
      'Beperkte concurrentie analyse',
    ],
    monthlyPriceId: process.env.REACT_APP_STRIPE_LIGHT_MONTHLY_PRICE_ID || 'price_1RTghDBsOwaHH4Y2lX1i3R8c',
    yearlyPriceId: process.env.REACT_APP_STRIPE_LIGHT_YEARLY_PRICE_ID || 'price_1RTghQBsOwaHH4Y2DkyOsJUf'
  },
  {
    name: 'Premium',
    description: 'Voor toegang tot alle aanbestedingen uit 5 landen',
    monthlyPrice: '12,95',
    yearlyPrice: '129,50',
    yearlySavings: '25,90',
    features: [
      'Alles van Light, plus:',
      'CPV codes instellen met een expert',
      'Internationale aanbestedingen',
      'Volledige concurrentie analyse',
      'Hulp bij inschrijven'
    ],
    highlighted: true,
    monthlyPriceId: process.env.REACT_APP_STRIPE_PREMIUM_MONTHLY_PRICE_ID || 'price_1RE7EUBsOwaHH4Y2NNo1ojzi',
    yearlyPriceId: process.env.REACT_APP_STRIPE_PREMIUM_YEARLY_PRICE_ID || 'price_1RE7FlBsOwaHH4Y2WAfzC5M8'
  }
];

const getUserPaymentInfoQuery = (userId: string) => `
  query GetUser {
    getUser(id: "${userId}") {
      id
      email
      stripeStatus
      stripe_id
    }
  }
`;

const Premium = () => {
    const [stripeCustomerId, setStripeCustomerId] = useState<string | null>(null);
    const [userStripeStatus, setUserStripeStatus] = useState<StripeStatus>(undefined);
    const [loading, setLoading] = useState<boolean>(false);
    const [isFetchingUser, setIsFetchingUser] = useState<boolean>(true);
    const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<'monthly' | 'yearly'>('monthly');

    useEffect(() => {
        document.title = "Premium | TenderTracer";
        const fetchUserData = async () => {
            setIsFetchingUser(true);
            try {
                const user = await Auth.currentAuthenticatedUser();
                const userId = user.attributes.sub;
                const gqlResponse: any = await API.graphql(
                    graphqlOperation(getUserPaymentInfoQuery(userId))
                );
                console.log("[PremiumPage] Fetched GQL Response:", gqlResponse);
                const userData = gqlResponse.data?.getUser;
                console.log("[PremiumPage] Fetched UserData:", userData);
                if (userData) {
                    setStripeCustomerId(userData.stripe_id);
                    setUserStripeStatus(userData.stripeStatus as StripeStatus);
                    console.log("[PremiumPage] userData.stripeStatus from API:", userData.stripeStatus);
                } else {
                    console.error("User data not found from GraphQL.");
                    setUserStripeStatus('free'); 
                }
            } catch (err) {
                console.error("Error fetching user data:", err);
                setUserStripeStatus('free'); 
            }
            setIsFetchingUser(false);
        };
        fetchUserData();
    }, []);

    console.log("[PremiumPage] Current userStripeStatus state before render:", userStripeStatus);

    const handleSubscribe = async (plan: PricingPlan) => {
        if (!stripeCustomerId) {
            console.error("Stripe Customer ID not found for user. Halting subscription.");
            alert("Er is een probleem met uw account configuratie. Neem contact op met support.");
            return;
        }
        setLoading(true);
        try {
            const priceID = selectedPlanPeriod === 'monthly' ? plan.monthlyPriceId : plan.yearlyPriceId;
            const session = await Auth.currentSession();
            const jwt = session.getIdToken().getJwtToken();

            const raw = JSON.stringify({
                customerID: stripeCustomerId,
                priceID: priceID,
                locale: "nl",
            });

            const requestOptions: RequestInit = {
                method: 'POST',
                headers: { ...API_DEFAULT_HEADERS, 'Authorization': `Bearer ${jwt}` },
                body: raw,
                redirect: 'follow'
            };

            const response = await fetch(`${API_DOMAIN}/stripe/pay`, requestOptions);
            const jsonResponse = await response.json();

            if (response.ok && jsonResponse.body) {
                window.location.replace(jsonResponse.body);
            } else {
                console.error("Stripe payment initiation failed:", jsonResponse);
                alert("Betaling kon niet worden gestart. Probeer het later opnieuw of neem contact op met support.");
                setLoading(false);
            }
        } catch (error) {
            console.error("Error during subscription process:", error);
            alert("Er is een onverwachte fout opgetreden. Probeer het later opnieuw of neem contact op met support.");
            setLoading(false);
        }
    };

    if (isFetchingUser) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spinner />
                <p className="ml-2">Abonnementsgegevens laden...</p>
            </div>
        );
    }

    return (
        <section className="h-full w-full overflow-y-auto relative p-5 bg-gray-50">
            {loading && (
                <div className="absolute inset-0 z-50 bg-black bg-opacity-30 flex flex-col items-center justify-center">
                    <Spinner />
                    <p className="pt-3 text-white">Betaling verwerken...</p>
                </div>
            )}

            <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">Kies uw Plan</h1>
                    <p className="text-lg text-gray-600">
                        Ontgrendel meer functies en krijg volledige toegang tot TenderTracer.
                    </p>
                    <div className="mt-8 flex justify-center">
                        <div className="relative bg-gray-200 p-1 rounded-lg inline-flex">
                            <button
                                onClick={() => setSelectedPlanPeriod('monthly')}
                                className={`relative px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${selectedPlanPeriod === 'monthly' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                                Maandelijks
                            </button>
                            <button
                                onClick={() => setSelectedPlanPeriod('yearly')}
                                className={`relative px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${selectedPlanPeriod === 'yearly' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-700 hover:bg-gray-100'}`}
                            >
                                Jaarlijks
                                <span className="absolute -top-2 -right-2 bg-primary-pink text-white text-xs px-2 py-0.5 rounded-full">
                                    Bespaar
                                </span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {pricingPlans.map((plan) => {
                        const isCurrentPlan =
                            (plan.name === 'Light' && userStripeStatus === 'light') ||
                            (plan.name === 'Premium' && userStripeStatus === 'premium');

                        let buttonText = `Start met ${plan.name}`;
                        let buttonAction = () => handleSubscribe(plan);
                        let buttonDisabled = false;
                        let buttonType: "primary" | "pink" | "secondary" = plan.highlighted ? "pink" : "primary";

                        if (isCurrentPlan) {
                            buttonText = "Huidig Plan";
                            buttonDisabled = true;
                            buttonType = "secondary";
                        } else if (plan.name === 'Premium' && userStripeStatus === 'light') {
                            buttonText = "Upgrade naar Premium";
                        } else if (plan.name === 'Light' && userStripeStatus === 'premium') {
                            buttonText = "Kies Light"; 
                        }

                        return (
                            <div
                                key={plan.name}
                                className={`relative rounded-2xl ${plan.highlighted ? 'border-2 border-primary-pink shadow-xl' : 'border border-gray-300'} p-6 md:p-8 bg-white flex flex-col min-h-[450px] ${isCurrentPlan ? 'opacity-70' : ''}`}
                            >
                                {plan.highlighted && !isCurrentPlan && (
                                    <span className="absolute top-0 right-6 -translate-y-1/2 bg-primary-pink text-white px-3 py-0.5 rounded-full text-sm font-semibold">
                                        Aanbevolen
                                    </span>
                                )}
                                {plan.highlighted && isCurrentPlan && (
                                    <span className="absolute top-0 right-6 -translate-y-1/2 bg-primary-pink text-white px-3 py-0.5 rounded-full text-sm font-semibold">
                                        Huidig Abonnement
                                    </span>
                                )}
                                <div className="pb-5 flex-grow">
                                    <div className="mb-6">
                                        <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                                        <p className="mt-2 text-gray-500 text-sm">{plan.description}</p>
                                    </div>
                                    <div className="mb-8">
                                        <p className="text-4xl font-bold text-gray-900">
                                            €{selectedPlanPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                                            <span className="text-lg font-normal text-gray-500">
                                                /{selectedPlanPeriod === 'monthly' ? 'maand' : 'jaar'}
                                            </span>
                                        </p>
                                        {selectedPlanPeriod === 'yearly' && (
                                            <p className="mt-1 text-sm text-primary-pink">
                                                Bespaar €{plan.yearlySavings} per jaar
                                            </p>
                                        )}
                                    </div>
                                    <ul className="space-y-3 flex-grow">
                                        {plan.features.map((feature) => (
                                            <li key={feature} className="flex items-start text-sm">
                                                <FontAwesomeIcon
                                                    icon={faCheck}
                                                    className={`mt-1 mr-3 flex-shrink-0 ${plan.highlighted ? 'text-primary-pink' : 'text-blue-600'}`}
                                                />
                                                <span className="text-gray-700">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="mt-auto pt-6">
                                    <Button
                                        type={buttonType}
                                        className="w-full justify-center py-3 text-base"
                                        onClick={buttonAction}
                                        disabled={buttonDisabled}
                                    >
                                        {buttonText}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Premium;
